import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mail, Users, Check, ChevronRight, ArrowLeft, Shield, Wrench, Zap, Droplets, Thermometer, Gauge, Volume2, Lightbulb, Settings, Box, Ruler } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import LocalServiceZone from '../components/LocalServiceZone';
import { formatPrice } from '../utils/formatPrice';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "contact@atlantiquespaboutique.com";

// Slugs des accessoires pour les options spa
const OPTION_SLUGS = {
  pompeChaleur: 'pompe-a-chaleur-spa-poolex-ospower-3kw',
  escalier: 'escalier-resine-2-marches-spa',
  leveCouverture: 'leve-couverture-simplelift-7-under'
};

const SpaDetailPage = () => {
  const { slug } = useParams();
  const [spa, setSpa] = useState(null);
  const [relatedSpas, setRelatedSpas] = useState([]);
  const [spaOptions, setSpaOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchSpa = async () => {
      try {
        const [spaRes, allSpasRes] = await Promise.all([
          axios.get(`${API}/spas/${slug}`),
          axios.get(`${API}/spas`)
        ]);
        setSpa(spaRes.data);
        
        // Get related spas (same category, different product)
        const related = allSpasRes.data
          .filter(s => s.category === spaRes.data.category && s.slug !== slug)
          .slice(0, 3);
        setRelatedSpas(related);

        // Fetch option accessory images from the database
        const optionPromises = Object.entries(OPTION_SLUGS).map(async ([key, optSlug]) => {
          try {
            const res = await axios.get(`${API}/parts/${optSlug}`);
            return [key, res.data];
          } catch {
            return [key, null];
          }
        });
        const optionResults = await Promise.all(optionPromises);
        const optionsMap = Object.fromEntries(optionResults.filter(([_, v]) => v !== null));
        setSpaOptions(optionsMap);
      } catch (error) {
        console.error('Error fetching spa:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSpa();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!spa) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Spa non trouvé</h1>
        <Link to="/spas">
          <Button>Voir tous les spas</Button>
        </Link>
      </div>
    );
  }

  // Build SEO-optimized title and description for spas
  const seoTitle = spa ? `${spa.name} – Spa ${spa.places} places | Atlantique Spa Boutique` : 'Spa';
  const seoDescription = spa ? 
    `${spa.name} - Spa ${spa.places} places. ${spa.description?.substring(0, 120)}... Installation Grand Ouest.` : 
    'Détails du spa';

  // JSON-LD Product schema for spas
  const spaProductSchema = spa ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": spa.name,
    "description": spa.description,
    "image": spa.images?.length > 0 ? spa.images : undefined,
    "brand": {
      "@type": "Brand",
      "name": spa.gamme || "Spa"
    },
    "category": `Spa ${spa.places} places`,
    "offers": {
      "@type": "Offer",
      "url": `https://atlantiquespaboutique.com/spas/${spa.slug}`,
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Atlantique Spa Boutique",
        "url": "https://atlantiquespaboutique.com",
        "areaServed": ["Loire-Atlantique", "Vendée", "Morbihan", "Maine-et-Loire"]
      }
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Nombre de places",
        "value": spa.places
      },
      {
        "@type": "PropertyValue",
        "name": "Gamme",
        "value": spa.gamme
      }
    ]
  } : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="spa-detail-page">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        canonical={spa ? `/spas/${spa.slug}` : '/spas'}
        type="product"
        image={spa?.images?.[0]}
        customSchema={spaProductSchema}
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/spas">Nos Spas</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{spa.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/spas" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Retour aux spas
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Images */}
          <div>
            <div className="bg-slate-100 rounded-xl overflow-hidden border border-slate-200 mb-4">
              <img 
                src={spa.images[selectedImage] || 'https://images.unsplash.com/photo-1574186907057-849802af13d9?w=800'} 
                alt={spa.name}
                className="w-full h-[400px] lg:h-[500px] object-contain bg-white"
              />
            </div>
            {spa.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto hide-scrollbar">
                {spa.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === i ? 'border-blue-600' : 'border-slate-200'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain bg-slate-50" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="mb-4">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-sm text-slate-500 uppercase tracking-wide">{spa.brand}</span>
                {spa.gamme && (
                  <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                    Gamme {spa.gamme}
                  </span>
                )}
              </div>
              {spa.in_stock && (
                <span className="badge-stock">En stock</span>
              )}
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid="spa-title">
              {spa.name}
            </h1>

            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                <Users className="w-5 h-5 text-slate-600" />
                <span className="font-medium">{spa.places} places</span>
              </div>
              <div className="bg-slate-100 px-3 py-2 rounded-lg">
                <span className="font-medium">{spa.dimensions}</span>
              </div>
            </div>

            <p className="text-slate-600 text-lg mb-6 leading-relaxed">
              {spa.description}
            </p>

            {/* Price */}
            <div className="bg-slate-100 rounded-xl p-6 mb-6">
              {spa.price_on_request ? (
                <div>
                  <div className="text-2xl font-bold text-orange-600 mb-2">Prix sur devis</div>
                  <p className="text-slate-600 text-sm">
                    Chaque projet est unique. Demandez votre devis personnalisé incluant l'étude de votre configuration.
                  </p>
                </div>
              ) : (
                <div>
                  <div className="text-3xl font-bold text-slate-900 mb-1">{formatPrice(spa.price)}</div>
                  <p className="text-slate-600 text-sm">Prix du spa. Installation sur devis selon configuration.</p>
                </div>
              )}
            </div>

            {/* Installation notice */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Installation sur demande par un professionnel</strong><br />
                Vous pouvez faire appel à un professionnel pour la livraison, la mise en place et le raccordement électrique de votre spa. Cette option est disponible sur demande lors de la validation de votre projet.
              </p>
            </div>

            {/* CTA */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row gap-3 mb-2">
                <Link to={`/devis?spa=${spa.slug}`} className="flex-1">
                  <Button size="lg" className="w-full bg-orange-500 hover:bg-orange-600 btn-lift" data-testid="spa-devis-btn">
                    Demander un Devis
                  </Button>
                </Link>
                <a href={`mailto:${EMAIL}`} className="flex-1">
                  <Button size="lg" variant="outline" className="w-full gap-2">
                    <Mail className="w-5 h-5" />
                    Contact
                  </Button>
                </a>
              </div>
              <p className="text-xs text-slate-500 text-center sm:text-left">
                Options personnalisables lors de l'étude de votre projet
              </p>
            </div>

            {/* Included */}
            {spa.included && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="font-semibold text-green-800 mb-1">Inclus dans le prix</div>
                <div className="text-green-700">{spa.included}</div>
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 0: POURQUOI CHOISIR CE SPA (Bénéfices concrets)
        ═══════════════════════════════════════════════════════════════════════ */}
        <div className="mt-12 bg-gradient-to-br from-blue-50 via-white to-orange-50 rounded-2xl border border-slate-200 p-6 md:p-8" data-testid="spa-benefits-section">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <Check className="w-6 h-6 text-green-600" />
            Pourquoi choisir ce spa ?
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Bénéfice 1 - Confort */}
            <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Confort optimal</h3>
              <p className="text-slate-600 text-sm">
                {spa.places} places ergonomiques avec {spa.specs?.jets || 'des'} jets de massage pour une relaxation complète.
              </p>
            </div>
            
            {/* Bénéfice 2 - Durabilité */}
            <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Durabilité garantie</h3>
              <p className="text-slate-600 text-sm">
                Structure garantie 10 ans, coque 5 ans. Composants de qualité pour un spa qui dure.
              </p>
            </div>
            
            {/* Bénéfice 3 - Entretien */}
            <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center mb-3">
                <Droplets className="w-5 h-5 text-cyan-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Entretien simplifié</h3>
              <p className="text-slate-600 text-sm">
                Filtration efficace et système de désinfection intégré pour une eau toujours propre.
              </p>
            </div>
            
            {/* Bénéfice 4 - Installation */}
            <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
                <Wrench className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Installation pro</h3>
              <p className="text-slate-600 text-sm">
                Livraison et mise en service par nos équipes en Loire-Atlantique, Vendée, Morbihan, Maine-et-Loire.
              </p>
            </div>
            
            {/* Bénéfice 5 - SAV */}
            <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                <Settings className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">SAV réactif</h3>
              <p className="text-slate-600 text-sm">
                Pièces détachées disponibles et équipe technique locale pour un suivi dans le temps.
              </p>
            </div>
            
            {/* Bénéfice 6 - Usage */}
            <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-3">
                <Zap className="w-5 h-5 text-indigo-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Prêt à l'emploi</h3>
              <p className="text-slate-600 text-sm">
                Spa complet avec couverture isolante, commandes intuitives et éclairage LED inclus.
              </p>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 1: CARACTÉRISTIQUES PRINCIPALES (Liste claire, une info par ligne)
        ═══════════════════════════════════════════════════════════════════════ */}
        <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-6 md:p-8" data-testid="spa-specs-section">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <Ruler className="w-6 h-6 text-blue-600" />
            Caractéristiques principales
          </h2>
          
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-1">
            {/* Ligne par ligne - format clair */}
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Nombre de places</span>
              <span className="font-semibold text-slate-900">{spa.places} places</span>
            </div>
            
            {spa.features?.some(f => f.includes('assise') || f.includes('allongée')) && (
              <div className="flex justify-between py-3 border-b border-slate-100">
                <span className="text-slate-600">Type de places</span>
                <span className="font-semibold text-slate-900">
                  {spa.features.find(f => f.includes('assise') || f.includes('allongée'))}
                </span>
              </div>
            )}
            
            {spa.specs?.jets && (
              <div className="flex justify-between py-3 border-b border-slate-100">
                <span className="text-slate-600">Nombre de jets</span>
                <span className="font-semibold text-slate-900">{spa.specs.jets} jets</span>
              </div>
            )}
            
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Dimensions</span>
              <span className="font-semibold text-slate-900">{spa.dimensions}</span>
            </div>
            
            {spa.specs?.volume && (
              <div className="flex justify-between py-3 border-b border-slate-100">
                <span className="text-slate-600">Volume d'eau</span>
                <span className="font-semibold text-slate-900">{spa.specs.volume}</span>
              </div>
            )}
            
            {spa.specs?.poids_vide && (
              <div className="flex justify-between py-3 border-b border-slate-100">
                <span className="text-slate-600">Poids à vide</span>
                <span className="font-semibold text-slate-900">{spa.specs.poids_vide}</span>
              </div>
            )}
            
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Structure</span>
              <span className="font-semibold text-slate-900">{spa.specs?.couleur_coque || 'Coque acrylique renforcée'}</span>
            </div>
            
            {spa.specs?.isolation && (
              <div className="flex justify-between py-3 border-b border-slate-100">
                <span className="text-slate-600">Isolation</span>
                <span className="font-semibold text-slate-900">{spa.specs.isolation}</span>
              </div>
            )}
            
            {spa.specs?.filtration && (
              <div className="flex justify-between py-3 border-b border-slate-100">
                <span className="text-slate-600">Système de filtration</span>
                <span className="font-semibold text-slate-900">{spa.specs.filtration}</span>
              </div>
            )}
            
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Chauffage</span>
              <span className="font-semibold text-slate-900">Réchauffeur électrique intégré</span>
            </div>
            
            {spa.specs?.eclairage && (
              <div className="flex justify-between py-3 border-b border-slate-100">
                <span className="text-slate-600">Éclairage</span>
                <span className="font-semibold text-slate-900">{spa.specs.eclairage}</span>
              </div>
            )}
            
            {spa.features?.some(f => f.includes('Plug') || f.includes('230V') || f.includes('400V')) && (
              <div className="flex justify-between py-3 border-b border-slate-100">
                <span className="text-slate-600">Alimentation électrique</span>
                <span className="font-semibold text-slate-900">
                  {spa.features.find(f => f.includes('Plug')) ? '230V - Plug & Play' : 
                   spa.features.find(f => f.includes('400V')) ? '400V triphasé' : '230V / 400V'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            TABLEAU RÉCAPITULATIF COMPACT
        ═══════════════════════════════════════════════════════════════════════ */}
        <div className="mt-8 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8f] rounded-2xl p-6 md:p-8 text-white" data-testid="spa-summary-table">
          <h2 className="text-lg font-bold mb-4 text-orange-400 uppercase tracking-wider text-sm">Résumé des caractéristiques</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-2xl font-bold text-white">{spa.places}</div>
              <div className="text-xs text-white/70 mt-1">Places</div>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-2xl font-bold text-white">{spa.specs?.jets || '-'}</div>
              <div className="text-xs text-white/70 mt-1">Jets</div>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-2xl font-bold text-white">{spa.specs?.pompes?.match(/\d+/)?.[0] || '1'}</div>
              <div className="text-xs text-white/70 mt-1">Pompe(s)</div>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-lg font-bold text-white">{spa.specs?.isolation ? 'Full Foam' : 'Standard'}</div>
              <div className="text-xs text-white/70 mt-1">Isolation</div>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-lg font-bold text-white">10 ans</div>
              <div className="text-xs text-white/70 mt-1">Garantie structure</div>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-lg font-bold text-white">5 ans</div>
              <div className="text-xs text-white/70 mt-1">Garantie coque</div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 2: ÉQUIPEMENTS INCLUS (Points forts techniques)
        ═══════════════════════════════════════════════════════════════════════ */}
        {(spa.specs?.pompes || spa.specs?.boitier || spa.specs?.desinfection || spa.specs?.bluetooth) && (
          <div className="mt-8 bg-slate-50 rounded-2xl border border-slate-200 p-6 md:p-8" data-testid="spa-equipment-section">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Settings className="w-6 h-6 text-orange-600" />
              Équipements inclus
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {spa.specs?.pompes && (
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Gauge className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-slate-900">Pompe(s)</span>
                  </div>
                  <p className="text-slate-600 text-sm">{spa.specs.pompes}</p>
                </div>
              )}
              
              {spa.specs?.pompe_circulation && (
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Droplets className="w-5 h-5 text-cyan-600" />
                    <span className="font-semibold text-slate-900">Circulation</span>
                  </div>
                  <p className="text-slate-600 text-sm">{spa.specs.pompe_circulation}</p>
                </div>
              )}
              
              {spa.specs?.boitier && (
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Box className="w-5 h-5 text-slate-600" />
                    <span className="font-semibold text-slate-900">Boîtier de contrôle</span>
                  </div>
                  <p className="text-slate-600 text-sm">{spa.specs.boitier}</p>
                </div>
              )}
              
              {spa.specs?.clavier && (
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Settings className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold text-slate-900">Clavier</span>
                  </div>
                  <p className="text-slate-600 text-sm">{spa.specs.clavier}</p>
                </div>
              )}
              
              {spa.specs?.desinfection && (
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-slate-900">Désinfection</span>
                  </div>
                  <p className="text-slate-600 text-sm">{spa.specs.desinfection}</p>
                </div>
              )}
              
              {spa.specs?.eclairage && (
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    <span className="font-semibold text-slate-900">Éclairage</span>
                  </div>
                  <p className="text-slate-600 text-sm">{spa.specs.eclairage}</p>
                </div>
              )}
              
              {spa.specs?.bluetooth && (
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Volume2 className="w-5 h-5 text-indigo-600" />
                    <span className="font-semibold text-slate-900">Audio Bluetooth</span>
                  </div>
                  <p className="text-slate-600 text-sm">{spa.specs.bluetooth}</p>
                </div>
              )}
              
              {spa.specs?.blower && (
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Zap className="w-5 h-5 text-orange-500" />
                    <span className="font-semibold text-slate-900">Blower</span>
                  </div>
                  <p className="text-slate-600 text-sm">{spa.specs.blower}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 3: POINTS FORTS (Arguments commerciaux séparés)
        ═══════════════════════════════════════════════════════════════════════ */}
        {spa.features && spa.features.length > 0 && (
          <div className="mt-8 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl border border-blue-100 p-6 md:p-8" data-testid="spa-features-section">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Check className="w-6 h-6 text-green-600" />
              Points forts de ce spa
            </h2>
            
            <ul className="grid sm:grid-cols-2 gap-3">
              {spa.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3 bg-white/70 rounded-lg p-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            SECTION 4: INSTALLATION & ACCOMPAGNEMENT
        ═══════════════════════════════════════════════════════════════════════ */}
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          {/* Installation */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-orange-600" />
              Installation sur demande
            </h3>
            <p className="text-slate-600 mb-4">
              Vous pouvez faire appel à un professionnel pour la livraison, la mise en place et le raccordement électrique de votre spa. Cette option est disponible sur demande lors de la validation de votre projet.
            </p>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                <span>Livraison et mise en place sur site</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                <span>Raccordement électrique vérifié</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                <span>Mise en service et formation à l'utilisation</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                <span>Zones d'intervention : Loire-Atlantique, Vendée, Morbihan, Maine-et-Loire</span>
              </li>
            </ul>
          </div>
          
          {/* Garantie & SAV */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Garanties constructeur
            </h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                <span>Pièces et main-d'œuvre : <strong>2 ans</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                <span>Coque acrylique : <strong>5 ans</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                <span>Structure du spa : <strong>10 ans</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                <span>SAV local et intervention rapide</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Options disponibles pour ce spa */}
        <div className="mt-16" data-testid="spa-options-section">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Options disponibles pour ce spa</h2>
          <p className="text-slate-600 mb-8">Ces accessoires complémentaires peuvent améliorer votre confort et votre expérience au quotidien.</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Pompe à chaleur */}
            <Link to={spaOptions.pompeChaleur ? `/pieces-detachees/${spaOptions.pompeChaleur.slug}` : '/accessoires-spa'}>
              <Card className="border-slate-200 hover:border-emerald-300 hover:shadow-lg transition-all overflow-hidden h-full">
                <div className="h-40 bg-white flex items-center justify-center border-b border-slate-100">
                  {spaOptions.pompeChaleur?.images?.[0] ? (
                    <img 
                      src={spaOptions.pompeChaleur.images[0]}
                      alt="Pompe à chaleur pour spa"
                      className="w-full h-full object-contain p-4"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400">
                      <Thermometer className="w-16 h-16" />
                    </div>
                  )}
                </div>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-900 mb-2">Pompe à chaleur pour spa</h3>
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                    Permet de réduire la consommation électrique et d'améliorer le confort d'utilisation.
                  </p>
                  <span className="inline-block text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full">
                    Option disponible en complément
                  </span>
                </CardContent>
              </Card>
            </Link>

            {/* Escalier de spa */}
            <Link to={spaOptions.escalier ? `/pieces-detachees/${spaOptions.escalier.slug}` : '/accessoires-spa'}>
              <Card className="border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all overflow-hidden h-full">
                <div className="h-40 bg-white flex items-center justify-center border-b border-slate-100">
                  {spaOptions.escalier?.images?.[0] ? (
                    <img 
                      src={spaOptions.escalier.images[0]}
                      alt="Escalier de spa"
                      className="w-full h-full object-contain p-4"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400">
                      <Box className="w-16 h-16" />
                    </div>
                  )}
                </div>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-900 mb-2">Escalier de spa</h3>
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                    Facilite l'accès au spa et améliore la sécurité au quotidien.
                  </p>
                  <span className="inline-block text-xs font-medium text-orange-700 bg-orange-50 px-3 py-1.5 rounded-full">
                    Option disponible en complément
                  </span>
                </CardContent>
              </Card>
            </Link>

            {/* Lève-couverture */}
            <Link to={spaOptions.leveCouverture ? `/pieces-detachees/${spaOptions.leveCouverture.slug}` : '/accessoires-spa'}>
              <Card className="border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all overflow-hidden h-full">
                <div className="h-40 bg-white flex items-center justify-center border-b border-slate-100">
                  {spaOptions.leveCouverture?.images?.[0] ? (
                    <img 
                      src={spaOptions.leveCouverture.images[0]}
                      alt="Lève-couverture"
                      className="w-full h-full object-contain p-4"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400">
                      <Shield className="w-16 h-16" />
                    </div>
                  )}
                </div>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-900 mb-2">Lève-couverture</h3>
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                    Permet de manipuler facilement la couverture sans effort.
                  </p>
                  <span className="inline-block text-xs font-medium text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full">
                    Option disponible en complément
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
          
          {/* SEO text */}
          <p className="text-sm text-slate-500 mt-6 text-center">
            Des options sont disponibles pour améliorer le confort, la consommation et l'usage de votre spa.
          </p>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            CONSEILS LIÉS - Maillage interne SEO
        ═══════════════════════════════════════════════════════════════════════ */}
        <div className="mt-16 bg-slate-50 rounded-2xl border border-slate-200 p-6 md:p-8" data-testid="spa-related-articles">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">Conseils pour bien choisir et entretenir votre spa</h2>
          <p className="text-slate-600 mb-6">Nos articles pour vous accompagner dans votre projet</p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link 
              to="/conseils/choisir-taille-spa-espace-usage" 
              className="bg-white rounded-xl p-4 border border-slate-100 hover:border-blue-300 hover:shadow-md transition-all group"
            >
              <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                Comment choisir la taille de son spa ?
              </h3>
              <p className="text-slate-600 text-sm">Espace, usage, nombre de personnes : tous les critères pour bien choisir.</p>
            </Link>
            
            <Link 
              to="/conseils/erreurs-installation-spa-eviter" 
              className="bg-white rounded-xl p-4 border border-slate-100 hover:border-orange-300 hover:shadow-md transition-all group"
            >
              <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                Erreurs d'installation à éviter
              </h3>
              <p className="text-slate-600 text-sm">Les pièges courants et comment les éviter pour une installation réussie.</p>
            </Link>
            
            <Link 
              to="/conseils/consommation-electrique-spa-guide-complet" 
              className="bg-white rounded-xl p-4 border border-slate-100 hover:border-green-300 hover:shadow-md transition-all group"
            >
              <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-green-600 transition-colors">
                Consommation électrique d'un spa
              </h3>
              <p className="text-slate-600 text-sm">Comprendre et optimiser la consommation de votre spa.</p>
            </Link>
          </div>
          
          <div className="mt-6 text-center">
            <Link to="/conseils" className="text-blue-600 hover:text-blue-700 font-medium text-sm inline-flex items-center gap-1">
              Voir tous nos conseils
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Related Products */}
        {relatedSpas.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Spas similaires</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedSpas.map((related) => (
                <Link key={related.id} to={`/spas/${related.slug}`}>
                  <Card className="card-hover border-slate-200 bg-white overflow-hidden h-full">
                    <div className="h-40 bg-white flex items-center justify-center">
                      <img 
                        src={related.images[0] || 'https://images.unsplash.com/photo-1574186907057-849802af13d9?w=400'} 
                        alt={related.name}
                        className="w-full h-full object-contain p-2"
                        loading="lazy"
                      />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-slate-900 mb-1 line-clamp-1">{related.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Users className="w-4 h-4" />
                        {related.places} places
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Local Service Zone Info for Spas */}
        <div className="mt-16">
          <LocalServiceZone />
        </div>
      </div>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t border-slate-200 md:hidden z-40">
        <div className="flex gap-2 mb-1">
          <Link to={`/devis?spa=${spa.slug}`} className="flex-1">
            <Button className="w-full bg-orange-500 hover:bg-orange-600">
              Demander un Devis
            </Button>
          </Link>
          <a href={`mailto:${EMAIL}`}>
            <Button variant="outline" size="icon">
              <Mail className="w-5 h-5" />
            </Button>
          </a>
        </div>
        <p className="text-[10px] text-slate-500 text-center">
          Options personnalisables lors de l'étude
        </p>
      </div>

      {/* Schema.org Product - Enriched */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": spa.name,
          "description": spa.description,
          "sku": spa.specs?.reference || spa.slug,
          "brand": { 
            "@type": "Brand", 
            "name": spa.brand || "Origin Spa"
          },
          "image": spa.images,
          "url": `https://atlantiquespaboutique.com/spas/${spa.slug}`,
          "category": spa.category,
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Nombre de places",
              "value": spa.places
            },
            {
              "@type": "PropertyValue",
              "name": "Dimensions",
              "value": spa.dimensions
            },
            spa.specs?.jets && {
              "@type": "PropertyValue",
              "name": "Nombre de jets",
              "value": spa.specs.jets
            }
          ].filter(Boolean),
          "offers": {
            "@type": "Offer",
            "availability": spa.in_stock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "price": spa.price || undefined,
            "priceCurrency": "EUR",
            "priceValidUntil": "2026-12-31",
            "itemCondition": "https://schema.org/NewCondition",
            "seller": {
              "@type": "Organization",
              "name": "Atlantique Spa Boutique",
              "url": "https://atlantiquespaboutique.com"
            },
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": "FR",
                "addressRegion": ["44", "85", "56", "49"]
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 7,
                  "maxValue": 21,
                  "unitCode": "DAY"
                }
              }
            }
          }
        })
      }} />
      
      {/* Schema.org BreadcrumbList */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Accueil",
              "item": "https://atlantiquespaboutique.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Nos Spas",
              "item": "https://atlantiquespaboutique.com/spas"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": spa.name,
              "item": `https://atlantiquespaboutique.com/spas/${spa.slug}`
            }
          ]
        })
      }} />
    </div>
  );
};

export default SpaDetailPage;
